import { ChangeDetectorRef, Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Country } from '../../Interface/Country';
import { States } from '../../Interface/States';
import { City } from '../../Interface/City';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToasterCommonService } from '../../service/toaster-common-service';
import { DomSanitizer } from '@angular/platform-browser';
import { ExtraFieldsService } from '../../service/extra-fields-service';

@Component({
  selector: 'app-signup-profile-edit',
  standalone: false,
  templateUrl: './signup-profile-edit.html',
  styleUrl: './signup-profile-edit.scss'
})
export class SignupProfileEdit implements OnInit {
  @Input() clearSearchInput = true;
  signupFielsListForm!: FormGroup;
  signupFieldsForm!: FormGroup;
  showAlert = false;
  form_step_unique_id!: string;
  event_id!: number;
  client_user_id!: number;
  event_link_with_client_id!: number;
  tenant_id!: number;
  all_extra_fields_arr: any[] = [];

  menu_bg_color!: string;
  menu_text_color!: string;
  button_color!: string;
  button_text_color!: string;
  inputText!: string;
  countryArr: Country[] = [];
  stateArr: States[] = [];
  state_count!: number;
  cityArr: City[] = [];
  city_count!: number;
  country_id!: number;
  state_id!: number;
  client_details: any[] = [];
  countrySubscription!: Subscription;
  loadingMap = false;
  countrySearchControl = new FormControl();
  countrySearchResults$!: Observable<any>;
  isProfileUpdating = false;

  public myError = (controlName: string, errorName: string) => {
    return this.signupFieldsForm.controls[controlName].hasError(errorName);
  };

  compareSelectValues = (a: any, b: any): boolean => {
    if (a === b) {
      return true;
    }
    if (a === null || a === undefined || b === null || b === undefined) {
      return false;
    }
    return String(a) === String(b);
  };

  private isOptionIdFieldType(fieldType: number): boolean {
    return [3, 6, 16, 18, 20, 21, 24].includes(fieldType);
  }

  private getDropDownValues(field: any): Array<{ id: number; option_value: string }> {
    const raw = field?.drop_down_value;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((o: any) => ({
        id: Number(o?.id),
        option_value: o?.option_value != null ? String(o.option_value) : '',
      }))
      .filter((o: { id: number; option_value: string }) => Number.isFinite(o.id));
  }

  private findOptionIdForRaw(
    options: Array<{ id: number; option_value: string }>,
    raw: unknown
  ): number | null {
    if (!options.length) {
      return null;
    }
    const n = Number(raw);
    if (Number.isFinite(n)) {
      const byNum = options.find((o) => Number(o.id) === n);
      if (byNum) {
        return Number(byNum.id);
      }
    }
    const s = String(raw ?? '').trim();
    if (!s) {
      return null;
    }
    const lower = s.toLowerCase();
    const hit = options.find((o) => String(o.option_value).trim().toLowerCase() === lower);
    return hit ? Number(hit.id) : null;
  }

  private resolveStoredValueToOptionId(field: any, raw: unknown): number | string {
    if (raw === null || raw === undefined || raw === '') {
      return '';
    }
    const fieldType = Number(field?.['field_master__field_type'] ?? field?.field_type);
    const options = this.getDropDownValues(field);
    if (options.length > 0) {
      const resolved = this.findOptionIdForRaw(options, raw);
      if (resolved != null) {
        return resolved;
      }
    }
    if ([18, 20, 21].includes(fieldType)) {
      const n = Number(raw);
      return Number.isFinite(n) ? n : String(raw);
    }
    if ([3, 6, 16, 24].includes(fieldType)) {
      const n = Number(raw);
      return Number.isFinite(n) ? n : '';
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : String(raw);
  }

  private findClientDetailForField(field: any): any {
    const name = field?.field_original_name;
    if (!name) {
      return {};
    }
    return (
      this.client_details.find((d) => d?.field_original_name === name) ||
      this.client_details.find(
        (d) =>
          String(d?.field_original_name).toLowerCase() === String(name).toLowerCase()
      ) ||
      {}
    );
  }

  private getInitialFieldValue(field: any, detail: any): any {
    const fieldType = Number(
      detail?.['field_master__field_type'] ?? field?.['field_type'] ?? field?.field_type
    );
    const merged = {
      ...field,
      ...detail,
      drop_down_value: field?.drop_down_value ?? detail?.drop_down_value,
      field_type: fieldType,
      field_master__field_type: fieldType,
    };
    if (this.isOptionIdFieldType(fieldType)) {
      const selected = detail?.['id_selected_value'];
      if (selected !== null && selected !== undefined && selected !== '') {
        return this.resolveStoredValueToOptionId(merged, selected);
      }
      return this.resolveStoredValueToOptionId(merged, detail?.['value']);
    }
    return detail?.['value'] ?? '';
  }

  private buildProfileUpdatePayload(): Record<string, unknown> {
    const raw = { ...this.signupFieldsForm.value } as Record<string, unknown>;
    raw['event_id'] = this.event_id;
    raw['client_user_id'] = this.client_user_id;
    raw['event_link_with_client_id'] = this.event_link_with_client_id;
    if (this.tenant_id) {
      raw['tenant_id'] = this.tenant_id;
    }
    for (const name of Object.keys(raw)) {
      const field = this.all_extra_fields_arr.find(
        (f: any) => f?.field_original_name === name
      );
      if (!field) {
        continue;
      }
      const ft = Number(field?.field_type);
      if (!this.isOptionIdFieldType(ft)) {
        continue;
      }
      raw[name] = this.resolveStoredValueToOptionId(field, raw[name]);
    }
    return raw;
  }

  constructor(
    public dialogRef: MatDialogRef<SignupProfileEdit>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private _cdref: ChangeDetectorRef,
    private spinner: NgxSpinnerService,
    private toastr: ToasterCommonService,
    public sanitizer: DomSanitizer,
    private extraFieldService: ExtraFieldsService
  ) {
    this.event_id = data.event_id;
    this.client_user_id = data.client_user_id;
    this.event_link_with_client_id = data.event_link_with_client_id;
    this.tenant_id = data.tenant_id;
  }

  ngOnInit() {
    this.signupFielsListForm = new FormGroup({
      form_step_unique_id: new FormControl(this.form_step_unique_id),
      event_id: new FormControl(this.event_id),
      client_user_id: new FormControl(this.client_user_id),
    });
    this.signupFieldsForm = new FormGroup({
      form_step_unique_id: new FormControl(this.form_step_unique_id),
      event_id: new FormControl(this.event_id),
      client_user_id: new FormControl(this.client_user_id),
      event_link_with_client_id: new FormControl(this.event_link_with_client_id),
      field_for: new FormControl(1),
      code: new FormControl(''),
    });
    this.getAllSignupFormField(this.signupFielsListForm.value);

    this.getAllCountryList();
  }

  getAllSignupFormField(formData: object) {
    this.spinner.show();
    this.extraFieldService.allSignupFieldList(formData).subscribe({
      next: (data) => {
        this.all_extra_fields_arr = data?.results ?? [];
        this.client_details = data?.client_custom_data ?? [];
        this.all_extra_fields_arr.forEach((item) => {
          const detail = this.findClientDetailForField(item);
          const fieldType = Number(item['field_type']);
          const initialValue = this.getInitialFieldValue(item, detail);
          const validators = item['is_required'] ? [Validators.required] : [];

          if (fieldType == 18) {
            this.country_id = initialValue;
            if (initialValue !== null && initialValue !== undefined && initialValue !== '') {
              this.changeCountry(initialValue);
            }
          } else if (fieldType == 21) {
            this.state_id = initialValue;
            if (initialValue !== null && initialValue !== undefined && initialValue !== '') {
              this.changeState(initialValue);
            }
          }

          this.signupFieldsForm.addControl(
            item['field_original_name'],
            new FormControl(initialValue, validators)
          );
        });
        this.spinner.hide();
        this._cdref.detectChanges();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.showError('Could not load profile fields.', '');
        this._cdref.detectChanges();
      },
    });
  }

  updateExtraField() {
    if (this.isProfileUpdating) {
      return;
    }
    if (this.signupFieldsForm.invalid) {
      this.signupFieldsForm.markAllAsTouched();
      this.toastr.showError('Please fill all required fields.', '');
      this._cdref.detectChanges();
      return;
    }
    this.isProfileUpdating = true;
    this.spinner.show();
    this.extraFieldService
      .updateSignupCompleted(this.buildProfileUpdatePayload())
      .pipe(
        finalize(() => {
          this.isProfileUpdating = false;
          this.spinner.hide();
          this._cdref.detectChanges();
        })
      )
      .subscribe({
        next: (data) => {
          const status = Number((data as any)?.status);
          if (status >= 200 && status < 300) {
            this.dialogRef.close({ action: true });
            this.toastr.showSuccess('Profile updated!', '');
          } else {
            const msg = (data as any)?.message;
            this.toastr.showError(
              typeof msg === 'string' && msg.trim() ? msg : 'Could not save profile.',
              ''
            );
          }
        },
        error: () => {
          this.toastr.showError('Could not save profile. Please try again.', '');
        },
      });
  }
  getAllCountryList() {
    const form_data = {};
    this.extraFieldService.getCountryList(form_data).subscribe((data) => {
      this.countryArr = data.results;
      this._cdref.detectChanges();
    });
  }

  changeCountry(event: any) {
    this.spinner.show();
    this.country_id = event;
    const form_data = {
      country_id: event,
    };
    this.extraFieldService.getStateList(form_data).subscribe((data) => {
      this.stateArr = data.results;
      this.state_count = data.count;
      this.spinner.hide();
      this._cdref.detectChanges();
    });
  }
  changeState(state_id: any) {
    this.spinner.show();
    this.state_id = state_id;
    const form_data = {
      state_id: this.state_id,
      country_id: this.country_id,
    };
    this.extraFieldService.getCityList(form_data).subscribe((data) => {
      this.cityArr = data.results;
      this.state_count = data.count;
      this.spinner.hide();
      this._cdref.detectChanges();
    });
  }

  countrySearchOption(value: string) {
    if (value) {
      this.countryArr = this.countryArr.filter((option) =>
        option.name.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.getAllCountryList();
    }
  }
  stateSearchOption(value: string) {
    if (value) {
      this.stateArr = this.stateArr.filter((option) =>
        option.name.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.changeCountry(this.country_id);
    }
  }
  citySearchOption(value: string) {
    if (value) {
      this.cityArr = this.cityArr.filter((option) =>
        option.name.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.changeState(this.state_id);
    }
  }
}
